import { useMemo } from 'react'
import { Sun, Moon, Monitor } from 'lucide-react'
import { useTheme, type Theme } from '@/lib/theme'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

export function ThemeToggle({ className, compact }: { className?: string; compact?: boolean }) {
  const { t } = useTranslation()
  const { theme, setTheme } = useTheme()
  const OPTIONS = useMemo<{ value: Theme; label: string }[]>(
    () => [
      { value: 'light', label: t('common.theme.light', { defaultValue: 'Light' }) },
      { value: 'dark', label: t('common.theme.dark', { defaultValue: 'Dark' }) },
      { value: 'system', label: t('common.theme.system', { defaultValue: 'Auto' }) },
    ],
    [t],
  )

  if (compact) {
    const idx = Math.max(0, OPTIONS.findIndex((o) => o.value === theme))
    const current = OPTIONS[idx]!
    const next = OPTIONS[(idx + 1) % OPTIONS.length]!
    return (
      <button
        type="button"
        onClick={() => setTheme(next.value)}
        aria-label={`${t('common.theme.label', { defaultValue: 'Theme' })}: ${current.label}`}
        title={current.label}
        className={cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-full border border-enterprise-border bg-enterprise-surface text-enterprise-muted hover:text-enterprise-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg',
          className,
        )}
      >
        {theme === 'light' ? (
          <Sun className="h-3.5 w-3.5" aria-hidden="true" />
        ) : theme === 'dark' ? (
          <Moon className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <Monitor className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>
    )
  }

  return (
    <div
      role="group"
      aria-label={t('common.theme.label', { defaultValue: 'Theme' })}
      className={cn(
        'inline-flex items-center rounded-full border border-enterprise-border bg-enterprise-surface p-0.5 shadow-none',
        className,
      )}
    >
      {OPTIONS.map((o) => {
        const active = theme === o.value
        const isAuto = o.value === 'system'
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            aria-label={o.label}
            title={o.label}
            onClick={() => setTheme(o.value)}
            className={cn(
              'inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-xs font-medium tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg',
              active
                ? `bg-enterprise-bg text-enterprise-text shadow-sm ${isAuto ? 'ring-1 ring-enterprise-indigo' : ''}`
                : 'text-enterprise-muted hover:bg-enterprise-raised hover:text-enterprise-text',
            )}
          >
            {o.value === 'light' ? (
              <Sun className="h-3.5 w-3.5" aria-hidden="true" />
            ) : o.value === 'dark' ? (
              <Moon className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Monitor className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            <span className="hidden 2xl:inline">{o.label}</span>
          </button>
        )
      })}
    </div>
  )
}
